import React, { useContext, useCallback } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useUpdateBook } from '@hooks/mutations/useUpdateBook'
import { AppContext } from '@state/context'
import { closePanel } from '@state/actions/panelsActions'
import { useItemSettings } from '@hooks/useItemSettings'
import { uploadImage } from '@helpers/api'
import { storagePath } from '@helpers/variables'
import { PANELS, DIVIDER_SIZE, BUTTON_TYPE, USER_ROLES } from '@helpers/constants'
import GridColumns from '@components/GridColumns'
import Divider from '@components/Divider'
import Form from '@components/Form'
import Loader from '@components/Loader'
import ButtonWithLoader from '@components/ButtonWithLoader'
import { setEditBookPanelStartOnTab } from '@state/actions/appActions'
import { useDeleteBook } from '@hooks/mutations/useDeleteBook'
import { useNavigate, useLocation } from 'react-router-dom'
import { useConfirm } from '@hooks/useConfirm'

const EditBookForm = (props) => {
  const [state, dispatch] = useContext(AppContext)
  const { confirm } = useConfirm()
  const {
    app: { userRole },
  } = state
  const { book } = props
  const {
    schoolYears,
    subjectOptions,
    levelOptions,
    tags,
    isLoading: itemSettingsLoading,
    error: itemSettingsError,
  } = useItemSettings()

  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { post, isPosting } = useUpdateBook()
  const { post: postDelete, isPosting: isPostingDelete } = useDeleteBook()

  const deleteBook = async () => {
    const confirmed = await confirm({
      title: book.name,
      text: 'Är du säker på att du vill radera denna bok? Den raderas helt och hållet, och försvinner från dina klasser/grupper.',
      confirmText: 'Ja, radera bok',
      cancelText: 'Avbryt',
    })

    if (confirmed) {
      const res = await postDelete({
        id: book.id,
      })

      if (res.success) {
        dispatch(closePanel(PANELS.EDIT_BOOK))
        if (pathname.split('/')[1] === 'bok') {
          navigate('/bokhylla')
        }
      }
    }
  }

  const { register, handleSubmit, errors, reset, control, formState } = useForm({
    defaultValues: {
      name: book.name,
      description: book.description,
      subject: book.subject.id,
      schoolYear: book.schoolYear.id,
      level: book.level.id,
      tags: book.tags,
    },
  })

  const onSubmit = async (data) => {
    let fileName = ''

    if (data?.image?.length > 0) {
      // User has selected new image, upload it!
      try {
        fileName = await uploadImage(data.image[0])
      } catch (error) {
        console.log(error)
        // show toast here?
      }
    } else if (book.image) {
      fileName = book.image
    }

    const tagIdsToConnect = data?.tags?.reduce((acc, tag) => {
      if (tag.hasOwnProperty('id')) {
        const alreadyPresent = book.tags.find(
          (existingTag) => existingTag.id === tag.id
        )

        if (!alreadyPresent) {
          return [...acc, tag.id]
        } else {
          return acc
        }
      } else {
        return acc
      }
    }, [])

    const tagIdsToDisconnect = book?.tags?.reduce(
      (acc, previouslyExistingTag) => {
        const stillPresent = data.tags.find(
          (tag) => tag.id === previouslyExistingTag.id
        )

        if (!stillPresent) {
          return [...acc, previouslyExistingTag.id]
        } else {
          return acc
        }
      },
      []
    )

    const tagsToCreate = data?.tags?.reduce((acc, tag) => {
      if (!tag.hasOwnProperty('id')) {
        return [...acc, { name: tag.name }]
      } else {
        return acc
      }
    }, [])

    const res = await post({
      id: book.id,
      name: data.name,
      description: data.description,
      subject: data.subject,
      schoolYear: data.schoolYear,
      level: data.level,
      connectTags: tagIdsToConnect,
      disconnectTags: tagIdsToDisconnect,
      createTags: tagsToCreate,
      image: fileName,
    })

    if (res.success) {
      dispatch(setEditBookPanelStartOnTab(null))
      dispatch(closePanel(PANELS.EDIT_BOOK))
    }
  }

  return (
    <>
      {itemSettingsLoading && <Loader />}
      {itemSettingsError && <p>Error loading item settings</p>}

      {!itemSettingsLoading && !itemSettingsError && (
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Label>Välj årskurs*</Form.Label>
          {schoolYears.map((schoolYear, index) => {
            const { id, name } = schoolYear
            return (
              <Form.Radio
                key={`schoolYear${index}`}
                name={'schoolYear'}
                id={name}
                ref={register({ required: true })}
                label={`Årskurs ${name}`}
                value={id}
              />
            )
          })}
          {errors.schoolYear && (
            <Form.ErrorMessage>Årskurs behövs</Form.ErrorMessage>
          )}

          {userRole === USER_ROLES.ADMIN &&
            <>
              <Divider size={DIVIDER_SIZE.SMALL} />

              <Form.Label htmlFor='image'>Välj en bild</Form.Label>
              <Form.ImageUpload
                name='image'
                id='image'
                ref={register()}
                imagePreviewUrl={
                  book.image
                    ? `${storagePath}/${book.image}`
                    : null
                }
              />
            </>
          }

          <Divider size={DIVIDER_SIZE.SMALL} />

          <GridColumns cols={2}>
            <div>
              <Form.Label htmlFor='subject'>Ämne*</Form.Label>
              <Form.Select
                name='subject'
                id='subject'
                ref={register({ required: true })}
                options={[{ value: '', name: 'Välj...' }, ...subjectOptions]}
              />
              {errors.subject && (
                <Form.ErrorMessage>Ämne behövs</Form.ErrorMessage>
              )}
            </div>
            <div>
              <Form.Label htmlFor='level'>Nivå*</Form.Label>
              <Form.Select
                name='level'
                id='level'
                ref={register({ required: true })}
                options={[{ value: '', name: 'Välj...' }, ...levelOptions]}
              />
              {errors.level && (
                <Form.ErrorMessage>Nivå behövs</Form.ErrorMessage>
              )}
            </div>
          </GridColumns>

          <Divider size={DIVIDER_SIZE.SMALL} />

          <Form.Label htmlFor='name'>Namnge boken*</Form.Label>
          <Form.Input
            type={'text'}
            name='name'
            id='name'
            ref={register({ required: true })}
            placeholder='Titel'
          />
          {errors.name && <Form.ErrorMessage>Titel behövs</Form.ErrorMessage>}

          <Divider size={DIVIDER_SIZE.SMALL} />

          <Form.Label htmlFor='name'>Beskriv boken</Form.Label>
          <Controller
            control={control}
            name="description"
            render={(
              { onChange, onBlur, value, name, ref },
              { invalid, isTouched, isDirty }
            ) => (
              <Form.Editor
                content={book?.description}
                handleChange={(value) => onChange(value)}
                elementId={`edit-book-description-${book?.id}`}
                // placeholder='Beskrivning'
              />
            )}
          />

          <Divider size={DIVIDER_SIZE.SMALL} />

          {userRole === USER_ROLES.ADMIN &&
            <>
              <Form.Label htmlFor='tags'>Lägg till taggar</Form.Label>
              <Controller
                control={control}
                name={'tags'}
                render={(
                  { onChange, onBlur, value, name, ref },
                  { invalid, isTouched, isDirty }
                ) => (
                  <Form.Tags
                    onChange={useCallback((tags) => {
                      onChange(tags)
                    }, [onChange])}
                    tags={book.tags}
                    suggestions={tags}
                  />
                )}
              />
            </>
          }

          <Divider size={DIVIDER_SIZE.SMALL} />

          <div className="flex">
            <ButtonWithLoader
              type={BUTTON_TYPE.PRIMARY}
              isLoading={isPosting}
              disabledText={'Uppdatera bok'}
              enabledText={'Uppdatera bok'}
              disabled={!formState.isDirty || isPosting}
              marginRight
            />

            <ButtonWithLoader
              type={BUTTON_TYPE.ALERT_RED_DARK}
              isLoading={isPostingDelete}
              enabledText={'Radera bok'}
              disabled={isPostingDelete}
              handleClick={deleteBook}
              nativeTypeAttribute={'button'}
              icon={'Exit'}
              iconRight
            />
          </div>
        </Form>
      )}
    </>
  )
}

export default EditBookForm
